import React, { useState } from 'react';
import {
  Container, Row, Col, Card, Badge, Button, Spinner, Alert
} from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import {
  listMyToolRequests,
  cancelToolRequest
} from '../../services/toolRequestService';
import DestructiveConfirmModal from '../../components/common/DestructiveConfirmModal';
import DashboardLayout from '../../components/layout/DashboardLayout';
import './ToolList.css';

const STATUS_META = {
  pending:   { label: 'Pendientes',  icon: 'bi-hourglass-split', color: 'warning', tone: 'text-warning' },
  approved:  { label: 'Aprobadas',   icon: 'bi-check-circle',    color: 'success', tone: 'text-success' },
  fulfilled: { label: 'Asignadas',   icon: 'bi-tools',           color: 'primary', tone: 'text-primary' },
  rejected:  { label: 'Rechazadas',  icon: 'bi-x-circle',        color: 'danger',  tone: 'text-danger'  },
  cancelled: { label: 'Canceladas',  icon: 'bi-slash-circle',    color: 'secondary', tone: 'text-muted' }
};

const ORDERED_STATUSES = ['pending', 'approved', 'fulfilled', 'rejected', 'cancelled'];

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('es-CR', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
  });
};

const MyToolRequests = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [cancelTarget, setCancelTarget] = useState(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['my-tool-requests', user?.id],
    queryFn: () => listMyToolRequests(),
    enabled: !!user?.id,
    staleTime: 30 * 1000
  });

  const cancelMutation = useMutation({
    mutationFn: ({ request_id }) => cancelToolRequest({ request_id }),
    onSuccess: ({ error: err, message }) => {
      if (err) { toast.error(err.message); return; }
      toast.success(message || 'Solicitud cancelada.');
      setCancelTarget(null);
      queryClient.invalidateQueries({ queryKey: ['my-tool-requests'] });
    },
    onError: (e) => { toast.error(e.message); setCancelTarget(null); }
  });

  const requests = data?.data || [];

  const grouped = ORDERED_STATUSES.reduce((acc, s) => {
    acc[s] = requests.filter(r => r.status === s);
    return acc;
  }, {});

  const renderRequestCard = (r) => {
    const meta = STATUS_META[r.status] || STATUS_META.pending;
    return (
      <Card key={r.id} className="mb-2 modern-card" style={{ borderLeft: `4px solid var(--bs-${meta.color})` }}>
        <Card.Body className="p-3">
          <div className="d-flex gap-3 align-items-start">
            {r.tool?.image_url ? (
              <img
                src={r.tool.image_url}
                alt=""
                style={{
                  width: 56, height: 56, objectFit: 'cover',
                  borderRadius: 8, flexShrink: 0, background: '#f8f9fa'
                }}
              />
            ) : (
              <div style={{
                width: 56, height: 56, background: '#f8f9fa',
                borderRadius: 8, display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0
              }}>
                <i className="bi bi-tools text-muted fs-4" />
              </div>
            )}

            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
                <div>
                  <strong>{r.tool?.name || '—'}</strong>
                  <code className="text-muted ms-2 small">{r.tool?.serial_number}</code>
                </div>
                <Badge bg={meta.color} className="d-flex align-items-center gap-1">
                  <i className={`bi ${meta.icon}`} />
                  {meta.label.replace(/s$/, '')}
                </Badge>
              </div>

              <div className="mt-2 small">
                {r.project && (
                  <div className="text-muted">
                    <i className="bi bi-briefcase me-1" />
                    {r.project.project_number
                      ? `${r.project.project_number} — `
                      : ''}{r.project.title}
                  </div>
                )}
                {r.requested_location && (
                  <div className="text-muted">
                    <i className="bi bi-geo-alt me-1" />
                    [{r.requested_location.type}] {r.requested_location.label}
                  </div>
                )}
                {r.reason && (
                  <div className="mt-1">
                    <i className="bi bi-chat-left-text text-muted me-1" />
                    {r.reason}
                  </div>
                )}
                <div className="text-muted mt-1">
                  <i className="bi bi-clock me-1" />
                  Enviada: {fmtDateTime(r.created_at)}
                </div>
                {r.approved_at && (
                  <div className="text-success">
                    <i className="bi bi-check2 me-1" />
                    {r.status === 'fulfilled' ? 'Asignada' : 'Aprobada'} el {fmtDateTime(r.approved_at)}
                    {r.approver?.full_name && <> por <strong>{r.approver.full_name}</strong></>}
                  </div>
                )}
                {r.status === 'rejected' && r.rejected_reason && (
                  <Alert variant="danger" className="mt-2 mb-0 py-2 small">
                    <strong>Motivo del rechazo:</strong> {r.rejected_reason}
                  </Alert>
                )}
                {r.status === 'fulfilled' && (
                  <div className="mt-2">
                    <Button
                      as={Link}
                      to="/mis-herramientas"
                      size="sm"
                      variant="outline-primary"
                    >
                      <i className="bi bi-tools me-1" />Ver en mis herramientas
                    </Button>
                  </div>
                )}
                {r.status === 'pending' && (
                  <div className="mt-2">
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => setCancelTarget(r)}
                    >
                      <i className="bi bi-x-lg me-1" />Cancelar solicitud
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  };

  return (
    <DashboardLayout>
    <Container fluid className="py-3">
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <Button
          variant="link"
          className="p-0 me-1 text-decoration-none"
          onClick={() => navigate('/mis-herramientas')}
          aria-label="Volver"
        >
          <i className="bi bi-arrow-left fs-4" />
        </Button>
        <h2 className="modern-title mb-0 d-flex align-items-center gap-2">
          <i className="bi bi-clipboard-check" />
          Mis solicitudes de herramientas
        </h2>
        <Button
          variant="outline-secondary"
          size="sm"
          className="ms-auto"
          onClick={() => refetch()}
          disabled={isLoading}
        >
          <i className="bi bi-arrow-clockwise me-1" />Refrescar
        </Button>
        <Button as={Link} to="/bodega" variant="primary" size="sm">
          <i className="bi bi-shop me-1" />Ir a bodega
        </Button>
      </div>

      {isLoading && (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      )}

      {error && (
        <Alert variant="danger">No se pudieron cargar las solicitudes: {error.message}</Alert>
      )}

      {!isLoading && requests.length === 0 && (
        <Alert variant="info" className="text-center py-4">
          <i className="bi bi-inbox fs-1 d-block mb-2 text-muted" />
          <h5>Sin solicitudes todavía.</h5>
          <p className="mb-3 text-muted">
            Las herramientas que requieren aprobación generan una solicitud
            cuando hacés clic en <strong>Solicitar</strong> desde la bodega.
          </p>
          <Button as={Link} to="/bodega" variant="primary">
            <i className="bi bi-shop me-1" />Explorar bodega
          </Button>
        </Alert>
      )}

      {!isLoading && requests.length > 0 && (
        <Row>
          <Col xs={12} md={10} lg={8}>
            {ORDERED_STATUSES.map(status => {
              const rows = grouped[status];
              if (!rows || rows.length === 0) return null;
              const meta = STATUS_META[status];
              return (
                <section key={status} className="mb-4">
                  <h5 className={`mb-2 ${meta.tone}`}>
                    <i className={`bi ${meta.icon} me-2`} />
                    {meta.label}
                    <Badge bg={meta.color} className="ms-2">{rows.length}</Badge>
                  </h5>
                  {rows.map(renderRequestCard)}
                </section>
              );
            })}
          </Col>
        </Row>
      )}

      <DestructiveConfirmModal
        show={!!cancelTarget}
        onHide={() => setCancelTarget(null)}
        title="Cancelar solicitud"
        icon="bi-x-octagon"
        variant="warning"
        description={
          <>
            Vas a cancelar tu solicitud para{' '}
            <strong>{cancelTarget?.tool?.name}</strong>.
            {cancelTarget?.tool?.serial_number && (
              <> <code className="ms-1">({cancelTarget.tool.serial_number})</code></>
            )}
          </>
        }
        consequences={[
          'La solicitud queda en estado "cancelada".',
          'El supervisor verá que ya no la necesitás.',
          'Podés volver a solicitar la herramienta más tarde si la necesitás.'
        ]}
        submitLabel="Confirmar cancelación"
        onConfirm={() => cancelTarget && cancelMutation.mutate({ request_id: cancelTarget.id })}
        pending={cancelMutation.isPending}
      />
    </Container>
    </DashboardLayout>
  );
};

export default MyToolRequests;
